import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Edit2, Loader2, Package, Plus, Search,
  Trash2, TrendingDown, X,
} from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { useProducts, type Product, type ProductInput } from '@/hooks/useProducts';
import { useChannels } from '@/hooks/useChannels';
import { formatBRL } from '@/lib/format';
import { downloadCsv } from '@/lib/export';
import { validateName, validateSKU, validatePrice, validatePositiveInt, validateDescription } from '@/lib/validators';
import { cn } from '@/lib/utils';
import { staggerContainer, staggerItem } from '@/lib/motion';

const STATUS_LABEL: Record<Product['status'], string> = {
  active:       'Ativo',
  inactive:     'Inativo',
  discontinued: 'Descontinuado',
};
const STATUS_CHIP: Record<Product['status'], string> = {
  active:       'chip-positive',
  inactive:     'chip-neutral',
  discontinued: 'chip-negative',
};

function stockChip(qty: number, min: number) {
  if (qty === 0)      return 'chip-negative';
  if (qty <= min)     return 'chip-warning';
  return 'chip-positive';
}
function stockLabel(qty: number, min: number) {
  if (qty === 0)  return 'Zerado';
  if (qty <= min) return 'Baixo';
  return 'OK';
}

// ─── Product Modal ────────────────────────────────────────────────────────────

const EMPTY_FORM: ProductInput = {
  name: '', sku: '', description: '',
  price_cents: 0, stock_qty: 0, min_stock: 5,
  channel_id: null, status: 'active',
};

function ProductModal({
  initial,
  channels,
  onSave,
  onClose,
}: {
  initial?: Product;
  channels: { id: string; name: string }[];
  onSave: (data: ProductInput) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<ProductInput>(
    initial
      ? {
          name: initial.name, sku: initial.sku ?? '',
          description: initial.description ?? '',
          price_cents: initial.price_cents,
          stock_qty: initial.stock_qty,
          min_stock: initial.min_stock,
          channel_id: initial.channel_id,
          status: initial.status,
        }
      : EMPTY_FORM
  );
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [saveErr, setSaveErr] = useState('');

  const set = (k: keyof ProductInput, v: unknown) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const nameErr  = validateName(form.name, { label: 'Nome do produto', max: 150 });
    const skuErr   = form.sku ? validateSKU(form.sku) : null;
    const priceErr = validatePrice(form.price_cents);
    const stockErr = validatePositiveInt(form.stock_qty, 'Estoque', 999_999);
    const minErr   = validatePositiveInt(form.min_stock, 'Estoque mínimo', 999_999);
    const descErr  = form.description ? validateDescription(form.description, 1000) : null;
    if (nameErr)  errs.name        = nameErr;
    if (skuErr)   errs.sku         = skuErr;
    if (priceErr) errs.price_cents = priceErr;
    if (stockErr) errs.stock_qty   = stockErr;
    if (minErr)   errs.min_stock   = minErr;
    if (descErr)  errs.description = descErr;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    setSaveErr('');
    try {
      await onSave(form);
      onClose();
    } catch (err: any) {
      setSaveErr(err.message ?? 'Erro ao salvar. Tente novamente.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        className="card w-full max-w-lg p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-ink text-lg">
            {initial ? 'Editar produto' : 'Novo produto'}
          </h2>
          <button onClick={onClose} className="btn-ghost !p-1.5" disabled={saving}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {saveErr && (
            <div className="px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
              {saveErr}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Nome *</label>
              <input className={cn('input w-full', errors.name && 'border-negative')} value={form.name}
                onChange={(e) => set('name', e.target.value)} maxLength={150} />
              {errors.name && <p className="text-xs text-negative mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">SKU</label>
              <input className={cn('input w-full', errors.sku && 'border-negative')} placeholder="SKU-001" value={form.sku ?? ''}
                onChange={(e) => set('sku', e.target.value)} maxLength={50} />
              {errors.sku && <p className="text-xs text-negative mt-1">{errors.sku}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Canal</label>
              <select className="input w-full" value={form.channel_id ?? ''}
                onChange={(e) => set('channel_id', e.target.value || null)}>
                <option value="">Sem canal</option>
                {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Preço (R$)</label>
              <CurrencyInput
                className={cn('input w-full', errors.price_cents && 'border-negative')}
                value={form.price_cents}
                onChange={(cents) => set('price_cents', cents)}
              />
              {errors.price_cents && <p className="text-xs text-negative mt-1">{errors.price_cents}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Status</label>
              <select className="input w-full" value={form.status}
                onChange={(e) => set('status', e.target.value as Product['status'])}>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="discontinued">Descontinuado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Estoque atual</label>
              <input type="number" min="0" max="999999" className={cn('input w-full', errors.stock_qty && 'border-negative')}
                value={form.stock_qty}
                onChange={(e) => set('stock_qty', parseInt(e.target.value || '0', 10))} />
              {errors.stock_qty && <p className="text-xs text-negative mt-1">{errors.stock_qty}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Estoque mínimo</label>
              <input type="number" min="0" max="999999" className={cn('input w-full', errors.min_stock && 'border-negative')}
                value={form.min_stock}
                onChange={(e) => set('min_stock', parseInt(e.target.value || '0', 10))} />
              {errors.min_stock && <p className="text-xs text-negative mt-1">{errors.min_stock}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Descrição</label>
              <textarea rows={2} className={cn('input w-full resize-none', errors.description && 'border-negative')}
                value={form.description ?? ''} maxLength={1000}
                onChange={(e) => set('description', e.target.value)} />
              {errors.description && <p className="text-xs text-negative mt-1">{errors.description}</p>}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Stock page ───────────────────────────────────────────────────────────────

export function Stock() {
  const { products, loading, fetchError, addProduct, updateProduct, deleteProduct } = useProducts();
  const { channels } = useChannels();

  const [search, setSearch]         = useState('');
  const [channelFilter, setChannel] = useState('');
  const [statusFilter, setStatus]   = useState('');
  const [stockFilter, setStock]     = useState('');
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState<Product | undefined>();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const [actionError, setActionError] = useState('');

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())
        && !p.sku?.toLowerCase().includes(search.toLowerCase())) return false;
      if (channelFilter && p.channel_id !== channelFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (stockFilter === 'out'  && p.stock_qty !== 0)            return false;
      if (stockFilter === 'low'  && !(p.stock_qty > 0 && p.stock_qty <= p.min_stock)) return false;
      if (stockFilter === 'ok'   && p.stock_qty <= p.min_stock)  return false;
      return true;
    });
  }, [products, search, channelFilter, statusFilter, stockFilter]);

  const totalItems  = products.reduce((s, p) => s + p.stock_qty, 0);
  const lowCount    = products.filter((p) => p.stock_qty > 0 && p.stock_qty <= p.min_stock).length;
  const outCount    = products.filter((p) => p.stock_qty === 0 && p.status === 'active').length;

  function openAdd()            { setEditing(undefined); setModalOpen(true); }
  function openEdit(p: Product) { setEditing(p);         setModalOpen(true); }
  function closeModal()         { setModalOpen(false);   setEditing(undefined); }

  async function handleSave(data: ProductInput) {
    if (editing) await updateProduct(editing.id, data);
    else await addProduct(data);
  }

  async function handleDeleteConfirm(id: string) {
    setDeleting(true);
    setActionError('');
    try {
      await deleteProduct(id);
      setConfirmDelete(null);
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setDeleting(false);
    }
  }

  function handleExport() {
    downloadCsv('estoque', ['Nome', 'SKU', 'Canal', 'Preço (R$)', 'Qtd.', 'Mín.', 'Status'],
      filtered.map((p) => [
        p.name,
        p.sku ?? '',
        p.channel_name ?? '',
        (p.price_cents / 100).toFixed(2),
        p.stock_qty,
        p.min_stock,
        STATUS_LABEL[p.status],
      ])
    );
  }

  return (
    <PageShell title="Estoque" subtitle="Controle de SKUs, quantidades e alertas de ruptura." maxWidth="xl">
      {/* Stats strip */}
      <motion.div
        className="grid grid-cols-3 gap-4 mb-6"
        variants={staggerContainer} initial="initial" animate="animate"
      >
        {[
          { label: 'Total de itens', value: totalItems, sub: `${products.length} produto(s)`, color: 'text-ink' },
          { label: 'Estoque baixo', value: lowCount, sub: 'abaixo do mínimo', color: 'text-warning' },
          { label: 'Zerados', value: outCount, sub: 'precisam de reposição', color: 'text-negative' },
        ].map((s) => (
          <motion.div key={s.label} variants={staggerItem} className="card p-4">
            <p className="text-xs text-ink-muted mb-1">{s.label}</p>
            <p className={cn('text-2xl font-bold tabular-nums', s.color)}>{s.value}</p>
            <p className="text-xs text-ink-soft mt-0.5">{s.sub}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Low-stock banner */}
      {outCount > 0 && (
        <div className="mb-4 card p-3 border-l-4 border-negative flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-negative shrink-0" />
          <p className="text-sm text-negative font-medium">
            {outCount} produto(s) com estoque zerado precisam de reposição urgente.
          </p>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
          <input className="input w-full pl-9" placeholder="Buscar por nome ou SKU…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <select className="input" value={channelFilter} onChange={(e) => setChannel(e.target.value)}>
          <option value="">Todos os canais</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="input" value={statusFilter} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="inactive">Inativo</option>
          <option value="discontinued">Descontinuado</option>
        </select>

        <select className="input" value={stockFilter} onChange={(e) => setStock(e.target.value)}>
          <option value="">Qualquer estoque</option>
          <option value="ok">Estoque OK</option>
          <option value="low">Estoque baixo</option>
          <option value="out">Zerado</option>
        </select>

        <button onClick={handleExport} className="btn-secondary shrink-0">Exportar CSV</button>
        <button onClick={openAdd} className="btn-primary gap-2 shrink-0">
          <Plus className="w-4 h-4" />Novo produto
        </button>
      </div>

      {/* Errors */}
      {(fetchError || actionError) && (
        <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
          <span>{fetchError || actionError}</span>
          <button onClick={() => setActionError('')} className="shrink-0 text-negative/60 hover:text-negative">✕</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map((i) => <div key={i} className="h-14 rounded-xl bg-surface-subtle animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title={products.length === 0 ? 'Nenhum produto cadastrado' : 'Nenhum resultado'}
          description={
            products.length === 0
              ? 'Cadastre seus produtos para acompanhar o estoque e receber alertas de ruptura.'
              : 'Tente ajustar os filtros de busca.'
          }
          action={products.length === 0
            ? <button onClick={openAdd} className="btn-primary gap-2"><Plus className="w-4 h-4" />Cadastrar produto</button>
            : undefined
          }
        />
      ) : (
        <motion.div
          className="card overflow-hidden"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wider">Produto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wider hidden md:table-cell">Canal</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-muted uppercase tracking-wider">Preço</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-ink-muted uppercase tracking-wider">Estoque</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-ink-muted uppercase tracking-wider hidden sm:table-cell">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-surface-subtle transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0 overflow-hidden">
                        {p.image_url
                          ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                          : <Package className="w-4 h-4 text-brand-700" />}
                      </div>
                      <div>
                        <p className="font-medium text-ink">{p.name}</p>
                        {p.sku && <p className="text-xs text-ink-muted font-mono">{p.sku}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-muted hidden md:table-cell">
                    {p.channel_name ?? <span className="text-ink-soft italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    {formatBRL(p.price_cents)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-bold tabular-nums text-ink">{p.stock_qty}</span>
                      <span className={cn('chip text-[11px]', stockChip(p.stock_qty, p.min_stock))}>
                        {stockLabel(p.stock_qty, p.min_stock)}
                      </span>
                    </div>
                    {p.stock_qty <= p.min_stock && p.stock_qty > 0 && (
                      <p className="text-[11px] text-ink-soft mt-0.5 flex items-center justify-center gap-0.5">
                        <TrendingDown className="w-3 h-3" />mín. {p.min_stock}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    <span className={cn('chip text-[11px]', STATUS_CHIP[p.status])}>
                      {STATUS_LABEL[p.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(p)}
                        className="btn-ghost !p-1.5 text-ink-muted hover:text-ink"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>

                      {confirmDelete === p.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => void handleDeleteConfirm(p.id)}
                            disabled={deleting}
                            className="text-xs text-negative font-medium hover:underline px-1"
                          >
                            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Excluir'}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="btn-ghost !p-1.5 text-ink-muted"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(p.id)}
                          className="btn-ghost !p-1.5 text-ink-muted hover:text-negative"
                          title="Excluir"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* Modal */}
      {modalOpen && (
        <ProductModal
          initial={editing}
          channels={channels}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </PageShell>
  );
}
